/**
 * BlockNote 블록 JSON → Markdown 문자열 (A2, lossy export).
 *
 * BlockNote의 blocksToMarkdownLossy에 대응. 라운드트립을 보장하지 않으며(에디터 고유
 * 속성·병합셀·정렬·색은 마크다운으로 표현 불가) "사람이 읽고 외부에 붙여넣을" 용도다.
 *
 * 지원: paragraph/heading(1~6)/bullet·numbered·check 리스트/quote/codeBlock/divider/
 *       pageBreak/table(GFM)/image/video·audio·file(링크)/
 *       toggle(헤딩·목록 children)/columnList(평탄화). 인라인: bold/italic/strike/code/link/
 *       hardbreak("\n").
 */

const LISTISH = new Set(["bulletListItem", "numberedListItem", "checkListItem"]);

function escapeMd(s) {
  return String(s == null ? "" : s).replace(/([\\`*_[\]])/g, "\\$1");
}

/** 인라인 콘텐츠 배열 → 마크다운. opts.cell=true면 줄바꿈을 공백으로(표 셀). */
export function inlineToMarkdown(content, opts) {
  opts = opts || {};
  if (!Array.isArray(content)) return "";
  let oln = 0; // 셀 내 순서목록 서수(best-effort)
  return content
    .map((node) => {
      if (node && node.type === "checkbox") {
        // 표 셀 체크박스는 GFM 태스크리스트로 표현(베스트에포트, MD→checkbox 역파싱은 범위 외).
        return node.checked ? "[x]" : "[ ]";
      }
      if (node && node.type === "olItem") { oln += 1; return `${oln}. `; }
      if (node && node.type === "ulItem") return "- ";
      if (node && node.type === "link") {
        const inner = inlineToMarkdown(node.content || [], opts);
        return `[${inner}](${node.href != null ? node.href : ""})`;
      }
      const raw = node && node.text != null ? node.text : "";
      const s = (node && node.styles) || {};
      if (s.code) {
        // 코드 마크: 원문 유지(백틱만 제거), 다른 마크와 결합하지 않음(lossy)
        const inner = String(raw).replace(/`/g, "");
        return "`" + (opts.cell ? inner.replace(/\n/g, " ") : inner) + "`";
      }
      let t = escapeMd(raw);
      t = opts.cell ? t.replace(/\n/g, " ") : t.replace(/\n/g, "  \n"); // hardbreak
      if (s.strike) t = `~~${t}~~`;
      if (s.italic) t = `*${t}*`;
      if (s.bold) t = `**${t}**`;
      return t;
    })
    .join("");
}

function codeBlockText(block) {
  return (block.content || []).map((n) => (n && n.text != null ? n.text : "")).join("");
}

function tableToMarkdown(block) {
  const tc = block.content;
  if (!tc || !Array.isArray(tc.rows) || !tc.rows.length) return "";
  const rows = tc.rows.map((r) =>
    (r.cells || []).map((c) => inlineToMarkdown((c && c.content) || [], { cell: true }).replace(/\|/g, "\\|") || " ")
  );
  const cols = rows.reduce((m, r) => Math.max(m, r.length), 0);
  const pad = (r) => {
    const a = r.slice();
    while (a.length < cols) a.push(" ");
    return a;
  };
  const line = (cells) => "| " + pad(cells).map((c) => c || " ").join(" | ") + " |";
  const header = line(rows[0]);
  const sep = "| " + new Array(cols).fill("---").join(" | ") + " |";
  const body = rows.slice(1).map(line);
  return [header, sep, ...body].join("\n");
}

function prefixLines(text, prefix) {
  return text
    .split("\n")
    .map((l) => prefix + l)
    .join("\n");
}

// columnList의 모든 자식(컬럼) 블록을 한 줄기로 평탄화
function flattenColumns(block) {
  const out = [];
  (block.children || []).forEach((col) => {
    (col.children || []).forEach((b) => out.push(b));
  });
  return out;
}

function renderBlock(block, depth, num) {
  const indent = "  ".repeat(depth);
  const type = block.type;
  const inline = () => inlineToMarkdown(block.content || []);
  let body = "";

  switch (type) {
    case "heading": {
      const level = Math.min(6, Math.max(1, (block.props && block.props.level) || 1));
      body = "#".repeat(level) + " " + inline();
      break;
    }
    case "bulletListItem":
      body = indent + "- " + inline();
      break;
    case "numberedListItem":
      body = indent + num + ". " + inline();
      break;
    case "checkListItem":
      body = indent + "- [" + (block.props && block.props.checked ? "x" : " ") + "] " + inline();
      break;
    case "quote":
      body = prefixLines(inline() || "", "> ");
      break;
    case "codeBlock":
      body = "```" + ((block.props && block.props.language) || "") + "\n" + codeBlockText(block) + "\n```";
      break;
    case "divider":
    case "pageBreak":
      body = "---";
      break;
    case "image": {
      const p = block.props || {};
      body = `![${p.name || p.caption || ""}](${p.url || ""})`;
      if (p.caption) body += `\n*${escapeMd(p.caption)}*`;
      break;
    }
    case "video":
    case "audio":
    case "file": {
      const p = block.props || {};
      body = `[${p.name || type}](${p.url || ""})`;
      break;
    }
    case "bookmark": {
      // 커스텀 블록 → 링크로 degrade(카드/임베드 모두).
      const p = block.props || {};
      body = `[${escapeMd(p.title || p.url || "link")}](${p.url || ""})`;
      break;
    }
    case "table":
      body = tableToMarkdown(block);
      break;
    case "columnList":
      return renderBlocks(flattenColumns(block), depth); // 평탄화(헤더/구분 없음)
    case "paragraph":
    default:
      body = inline();
      break;
  }

  // children: 리스트/토글 등 중첩 → 한 단계 들여써서 이어붙임
  const kids = block.children;
  if (Array.isArray(kids) && kids.length && type !== "columnList") {
    const isList = LISTISH.has(type);
    const childMd = renderBlocks(kids, isList ? depth + 1 : depth);
    if (childMd) body += (isList ? "\n" : "\n\n") + childMd; // 리스트=타이트, 토글 등=빈 줄
  }
  return body;
}

function renderBlocks(blocks, depth) {
  if (!Array.isArray(blocks)) return "";
  const parts = [];
  let num = 0;
  for (const b of blocks) {
    if (!b || !b.type) continue;
    if (b.type === "numberedListItem") num += 1;
    else num = 0;
    parts.push({ type: b.type, md: renderBlock(b, depth, num || 1) });
  }
  let s = "";
  for (let i = 0; i < parts.length; i++) {
    if (i > 0) {
      const a = parts[i - 1].type;
      const c = parts[i].type;
      s += LISTISH.has(a) && LISTISH.has(c) ? "\n" : "\n\n";
    }
    s += parts[i].md;
  }
  return s;
}

/** 블록 배열 → Markdown 문자열(끝에 개행 1개). */
export function blocksToMarkdown(blocks) {
  const body = renderBlocks(blocks || [], 0).replace(/\s+$/, "");
  return body ? body + "\n" : "";
}

// ───────────────────────────── 가져오기(import) ─────────────────────────────
// Markdown 문자열 → BlockNote 블록 배열 (A2, lossy). 라인 기반 + 인라인 재귀 파서.

const BASE_P = () => ({ textColor: "default", backgroundColor: "default", textAlignment: "left" });

function mergeAdjacent(items) {
  const out = [];
  for (const it of items) {
    const prev = out[out.length - 1];
    if (prev && prev.type === "text" && it.type === "text" && JSON.stringify(prev.styles) === JSON.stringify(it.styles))
      prev.text += it.text;
    else out.push(it);
  }
  return out;
}

// 인라인 패턴(슬라이스 시작에 앵커). 순서: code → 굵게(**,__) → 취소 → 기울임(*,_) → 링크
const INLINE_PATTERNS = [
  { re: /^`([^`]+)`/, style: "code", raw: true },
  { re: /^\*\*([\s\S]+?)\*\*/, style: "bold" },
  { re: /^__([\s\S]+?)__/, style: "bold" },
  { re: /^~~([\s\S]+?)~~/, style: "strike" },
  { re: /^\*([\s\S]+?)\*/, style: "italic" },
  { re: /^_([\s\S]+?)_/, style: "italic" },
  { re: /^\[([^\]]*)\]\(([^)\s]+)\)/, link: true },
];

/** Markdown 인라인 텍스트 → inline content 배열. */
export function parseInlineMarkdown(text) {
  const out = [];
  let buf = "";
  let i = 0;
  const flush = () => {
    if (buf) out.push({ type: "text", text: buf, styles: {} });
    buf = "";
  };
  while (i < text.length) {
    const slice = text.slice(i);
    let hit = null;
    for (const p of INLINE_PATTERNS) {
      const m = p.re.exec(slice);
      if (m) {
        hit = { p, m };
        break;
      }
    }
    if (!hit) {
      buf += text[i];
      i++;
      continue;
    }
    flush();
    const { p, m } = hit;
    if (p.link) {
      out.push({ type: "link", href: m[2], content: mergeAdjacent(parseInlineMarkdown(m[1])) });
    } else if (p.raw) {
      out.push({ type: "text", text: m[1], styles: { code: true } });
    } else {
      const inner = parseInlineMarkdown(m[1]);
      for (const n of inner) {
        if (n.type === "text") n.styles = { ...n.styles, [p.style]: true };
        out.push(n);
      }
    }
    i += m[0].length;
  }
  flush();
  return mergeAdjacent(out);
}

const HR_RE = /^(-{3,}|\*{3,}|_{3,})$/;
const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const LIST_RE = /^(\s*)([-*+]|\d+\.)\s+(.*)$/;
const FENCE_RE = /^```(.*)$/;

function isBlockStart(line) {
  const t = line.trim();
  return (
    t === "" ||
    HR_RE.test(t) ||
    HEADING_RE.test(line) ||
    LIST_RE.test(line) ||
    /^>\s?/.test(line) ||
    FENCE_RE.test(t) ||
    /^\|.*\|$/.test(t)
  );
}

function listItemBlock(marker, rest) {
  let type, props;
  const check = /^\[([ xX])\]\s+(.*)$/.exec(rest);
  if (/^\d+\./.test(marker)) {
    type = "numberedListItem";
    props = BASE_P();
    const n = parseInt(marker, 10);
    if (n && n !== 1) props.start = n;
  } else if (check) {
    type = "checkListItem";
    props = { ...BASE_P(), checked: check[1].toLowerCase() === "x" };
    rest = check[2];
  } else {
    type = "bulletListItem";
    props = BASE_P();
  }
  return { type, props, content: parseInlineMarkdown(rest), children: [] };
}

function joinParagraph(lines) {
  let s = "";
  for (let k = 0; k < lines.length; k++) {
    if (k > 0) s += /\s{2,}$/.test(lines[k - 1]) ? "\n" : " "; // 줄끝 2칸 = hardbreak
    s += lines[k].replace(/\s+$/, "");
  }
  return s;
}

/** Markdown 문자열 → BlockNote 블록 배열. */
export function markdownToBlocks(md) {
  const lines = String(md == null ? "" : md).replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // 펜스 코드블록
    const fence = FENCE_RE.exec(trimmed);
    if (fence) {
      const lang = fence[1].trim();
      const code = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) {
        code.push(lines[i]);
        i++;
      }
      i++; // 닫는 펜스
      blocks.push({
        type: "codeBlock",
        props: { language: lang },
        content: code.length ? [{ type: "text", text: code.join("\n"), styles: {} }] : [],
        children: [],
      });
      continue;
    }

    if (trimmed === "") {
      i++;
      continue;
    }

    if (HR_RE.test(trimmed)) {
      blocks.push({ type: "divider", props: {}, content: [], children: [] });
      i++;
      continue;
    }

    const h = HEADING_RE.exec(line);
    if (h) {
      blocks.push({ type: "heading", props: { ...BASE_P(), level: h[1].length }, content: parseInlineMarkdown(h[2]), children: [] });
      i++;
      continue;
    }

    // 인용(연속 > 묶음)
    if (/^>\s?/.test(line)) {
      const q = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        q.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ type: "quote", props: BASE_P(), content: parseInlineMarkdown(joinParagraph(q)), children: [] });
      continue;
    }

    // 표(헤더행 + 구분행)
    if (/^\|.*\|$/.test(trimmed) && i + 1 < lines.length && /^\|?[\s:|-]*-{2,}[\s:|-]*\|?$/.test(lines[i + 1].trim())) {
      const splitRow = (l) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
      const rowsRaw = [splitRow(lines[i])];
      i += 2; // 헤더 + 구분행
      while (i < lines.length && /^\|.*\|$/.test(lines[i].trim())) {
        rowsRaw.push(splitRow(lines[i]));
        i++;
      }
      const cols = rowsRaw.reduce((m, r) => Math.max(m, r.length), 0);
      const rows = rowsRaw.map((cells) => {
        const padded = cells.slice();
        while (padded.length < cols) padded.push("");
        return {
          cells: padded.map((c) => ({
            type: "tableCell",
            props: { colspan: 1, rowspan: 1 },
            content: parseInlineMarkdown(c.replace(/\\\|/g, "|")),
          })),
        };
      });
      blocks.push({
        type: "table",
        props: {},
        content: { type: "tableContent", columnWidths: new Array(cols).fill(null), rows },
        children: [],
      });
      continue;
    }

    // 단독 이미지
    const img = /^!\[([^\]]*)\]\(([^)\s]+)\)$/.exec(trimmed);
    if (img) {
      const props = { url: img[2] };
      if (img[1]) {
        props.name = img[1];
        props.caption = img[1];
      }
      blocks.push({ type: "image", props, content: [], children: [] });
      i++;
      continue;
    }

    // 리스트(연속 항목; 들여쓰기 2칸당 한 단계 중첩)
    const li = LIST_RE.exec(line);
    if (li) {
      const stack = []; // [{ indent, block }]
      while (i < lines.length && LIST_RE.test(lines[i])) {
        const mm = LIST_RE.exec(lines[i]);
        const indent = mm[1].replace(/\t/g, "  ").length;
        const block = listItemBlock(mm[2], mm[3]);
        while (stack.length && stack[stack.length - 1].indent >= indent) stack.pop();
        if (stack.length) stack[stack.length - 1].block.children.push(block);
        else blocks.push(block);
        stack.push({ indent, block });
        i++;
      }
      continue;
    }

    // 문단(빈 줄 또는 다음 블록 시작 전까지)
    const para = [line];
    i++;
    while (i < lines.length && lines[i].trim() !== "" && !isBlockStart(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    blocks.push({ type: "paragraph", props: BASE_P(), content: parseInlineMarkdown(joinParagraph(para)), children: [] });
  }
  return blocks;
}
